import type { FC } from 'react';
import { motion } from 'framer-motion';

import { heroReveal } from '#/lib/hero-reveal';
import type { SanityHeroSplit } from '#/lib/sanity/queries/heroSplit';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { SectionMascot } from '#/components/BlobMascot';
import { Button } from '#/components/Button';
import { ButtonRow } from '#/components/ButtonRow';
import { FormCustom } from '#/components/Form/Custom';
import { HomepageEmailForm } from '#/components/Form/HomepageEmail';
import { formatHeadline } from '#/components/Headline';
import {
  getHeroBackground,
  HeroBackgroundGraphic,
} from '#/components/HeroBackgroundGraphic';
import { Media } from '#/components/Media';
import { SimpleRichText } from '#/components/SimpleRichText';

export const HeroSplit: FC<SanityHeroSplit & PageLayoutSharedProps> = (
  props,
) => {
  const isDark = isDarkSectionTheme(props.theme);
  const isFirst = props.sectionIndex === 0;
  const mediaLeft = props.mediaPosition === 'left';
  const accentClass = getSectionThemeAccent(props.theme);
  const buttonTheme = getSectionButtonTheme(props.theme);
  // Charcoal gets dark form controls; purple/white stay light (same as HeroForm).
  const formTheme = props.theme === 'charcoal' ? 'dark' : 'light';
  const background = getHeroBackground(props.theme, props.backgroundPattern);
  const ctaMode = props.ctaMode ?? 'buttons';

  return (
    <div
      className={cn(
        'p-main relative isolate overflow-hidden',
        getSectionThemeSurface(props.theme),
        isFirst
          ? 'mt-[calc(var(--header-height)*-1)] pb-60 pt-[calc(var(--header-height)+6rem)] m:pb-100'
          : 'py-60 m:py-100',
      )}
      style={background ? { backgroundColor: background.baseColor } : undefined}
    >
      {background ? <HeroBackgroundGraphic background={background} /> : null}
      <SectionMascot preset={props.mascot} />
      <div className="relative z-[1] mx-auto flex max-w-1280 flex-col gap-40 m:flex-row m:items-center m:gap-80">
        <div
          className={cn(
            'flex flex-1 flex-col gap-24 m:gap-32',
            mediaLeft && 'm:order-2',
          )}
        >
          {props.eyebrow ? (
            <motion.p
              {...heroReveal(0)}
              className={cn('type-eyebrow', accentClass)}
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'eyebrow',
              )}
            >
              {props.eyebrow}
            </motion.p>
          ) : null}

          <div className="flex flex-col gap-16 m:gap-20">
            <motion.h1
              {...heroReveal(1)}
              className="type-heading3 m:type-heading2 l:type-heading1 text-balance"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'title',
              )}
            >
              {formatHeadline(props.title, accentClass)}
            </motion.h1>

            {props.bodyCopy?.length ? (
              <motion.div {...heroReveal(2)}>
                <SimpleRichText
                  value={props.bodyCopy}
                  highlightClass={accentClass}
                  className={cn(
                    'max-w-[540px]',
                    isDark ? 'text-cloud' : 'text-grayscale-05',
                  )}
                />
              </motion.div>
            ) : null}
          </div>

          {ctaMode === 'buttons' && props.buttons?.length ? (
            <motion.div
              {...heroReveal(3)}
              className="@container w-full"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'buttons',
              )}
            >
              <ButtonRow buttons={props.buttons} theme={buttonTheme} />
            </motion.div>
          ) : null}

          {ctaMode === 'standardForm' && props.form ? (
            <motion.div
              {...heroReveal(3)}
              className="w-full max-w-[500px]"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'form',
              )}
            >
              <FormCustom
                className={cn(
                  isDark &&
                    '[&_.legal-text]:!text-cloud [&_.step-indicator]:!text-cloud',
                )}
                form={props.form}
                theme={formTheme}
                buttonTheme={buttonTheme}
                confirmation={props.formConfirmation ?? ''}
              />
            </motion.div>
          ) : null}

          {ctaMode === 'redirectForm' &&
          (props.formFunnel === 'rails' ||
            props.form?.formType === 'contact') ? (
            <motion.div
              {...heroReveal(3)}
              className="flex w-full flex-col gap-16 m:flex-row m:items-start m:gap-32"
            >
              <HomepageEmailForm
                className="w-full max-w-[500px]"
                form={props.form?.formType === 'contact' ? props.form : null}
                formFunnel={props.formFunnel ?? 'contact'}
                primaryButtonLabel={props.primaryButtonLabel ?? 'Get a demo'}
                theme={formTheme}
                legalTextClassName={cn(isDark && '!text-cloud')}
              />
              {props.formFunnel !== 'rails' && props.secondaryButton?.href ? (
                <Button
                  variant="secondary"
                  theme={formTheme}
                  href={props.secondaryButton.href}
                  target={props.secondaryButton.target}
                  className="hidden h-50 items-center justify-center self-start text-center m:flex"
                  data-sanity={createSanityFieldDataAttribute(
                    props.sanitySource,
                    'secondaryButton',
                  )}
                >
                  {props.secondaryButton.title}
                </Button>
              ) : null}
            </motion.div>
          ) : null}
        </div>

        <motion.div
          {...heroReveal(4)}
          className={cn('flex-1', mediaLeft && 'm:order-1')}
          data-sanity={createSanityFieldDataAttribute(
            props.sanitySource,
            'media',
          )}
        >
          <div className="overflow-hidden rounded-16">
            <Media
              media={props.media}
              sizes={['100vw', { m: '50vw', '1300px': '600px' }]}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
